/**
 * Argus — toast global pentru notificare agent connect/disconnect real-time.
 *
 * Mount-uit în App.tsx. Ascultă CustomEvent `argus:agent-status` (emit de wsClient
 * la primire `agent_status_push`) → MUI Snackbar top-right cu auto-dismiss 5s.
 *
 * Click toast → set selectedAgentId în Zustand store + navigate /agents.
 *
 * La fiecare event invalidate query `['agents']` pentru AgentSidebar refresh instant.
 *
 * Bloc agent-status-realtime-push 2026-05-06.
 */

import { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSidebarStore } from '../store/sidebarStore';

const AGENT_STATUS_CUSTOM_EVENT = 'argus:agent-status';

interface AgentStatusPayload {
  readonly type: 'agent_status_push';
  readonly agentId: string;
  readonly hostname: string | null;
  readonly status: 'online' | 'offline';
  readonly reason: string | null;
  readonly timestamp: string;
}

interface ToastEntry {
  readonly id: number;
  readonly payload: AgentStatusPayload;
}

let nextToastId = 1;

function buildMessage(p: AgentStatusPayload): string {
  const host = p.hostname ?? p.agentId.slice(0, 8);
  if (p.status === 'online') {
    return `Agent online: ${host}`;
  }
  const reason = p.reason ? ` (${p.reason})` : '';
  return `Agent offline: ${host}${reason}`;
}

export function AgentStatusToast(): JSX.Element {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const navigate = useNavigate();
  const setSelectedAgentId = useSidebarStore((s) => s.setSelectedAgentId);
  const queryClient = useQueryClient();

  const handleEvent = useCallback((event: Event) => {
    const ce = event as CustomEvent<AgentStatusPayload>;
    if (!ce.detail) return;
    const id = nextToastId++;
    setEntries((prev) => [...prev, { id, payload: ce.detail }]);
    // Invalidate agents query → AgentSidebar refresh status icon imediat
    void queryClient.invalidateQueries({ queryKey: ['agents'] });
  }, [queryClient]);

  useEffect(() => {
    window.addEventListener(AGENT_STATUS_CUSTOM_EVENT, handleEvent);
    return () => window.removeEventListener(AGENT_STATUS_CUSTOM_EVENT, handleEvent);
  }, [handleEvent]);

  function handleClose(id: number): void {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }

  function handleClick(payload: AgentStatusPayload, id: number): void {
    setSelectedAgentId(payload.agentId);
    navigate('/agents');
    handleClose(id);
  }

  return (
    <Stack
      sx={{
        position: 'fixed',
        top: 20,
        left: 20,
        zIndex: 1500,
        gap: 1,
        maxWidth: 380,
      }}
    >
      {entries.map((entry, idx) => (
        <Snackbar
          key={entry.id}
          open
          autoHideDuration={5000}
          onClose={() => handleClose(entry.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{
            position: 'static',
            transform: `translateY(${idx * 4}px)`,
          }}
        >
          <Alert
            severity={entry.payload.status === 'online' ? 'success' : 'warning'}
            onClick={() => handleClick(entry.payload, entry.id)}
            onClose={() => handleClose(entry.id)}
            sx={{ cursor: 'pointer', minWidth: 280 }}
            variant="filled"
          >
            <Typography variant="body2" component="div">
              {buildMessage(entry.payload)}
            </Typography>
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
}
